/**
 * 车辆报告
 */
import React, { useState } from 'react';
import { Card, Form, Row, Col, Button, Input, Select, message } from 'antd';
import dayjs from 'dayjs';
import CommonTable from '@/components/commonTable';
import ExportButton from '@/components/exportButton';
import DatePicker from '@/components/datePicker';
import { useTable } from '@/utils/useTable';
import { carRiskListRequest, carRiskListRequestPath, CarRiskScoreRange } from '@/service/carRisk';
import styles from '@/styles/pageContent.module.scss';

const { Option } = Select;

/** 业务模式选项 */
const MODE_OPTIONS = [
  { label: '中富模式', value: '中富模式' },
  { label: '保险模式', value: '保险模式' },
];

/** 车辆等级选项 */
const RISK_LEVEL_OPTIONS = [
  { label: '低风险', value: '低风险' },
  { label: '中风险', value: '中风险' },
  { label: '高风险', value: '高风险' },
];

/** 车辆评分区间选项（业务展示值 -> 接口传值）
 * 后端接口为左开右闭 (start, end]，业务需求：
 * - 1-10 展示为 1-10，实际传值 start=-1, end=10
 * - 其余区间展示值与传值一致
 */
const SCORE_RANGE_OPTIONS: { label: string; value: CarRiskScoreRange }[] = [
  { label: '1-10', value: { start: -1, end: 10 } },
  { label: '10-20', value: { start: 10, end: 20 } },
  { label: '20-30', value: { start: 20, end: 30 } },
  { label: '30-40', value: { start: 30, end: 40 } },
  { label: '40-50', value: { start: 40, end: 50 } },
  { label: '50-60', value: { start: 50, end: 60 } },
  { label: '60-70', value: { start: 60, end: 70 } },
  { label: '70-80', value: { start: 70, end: 80 } },
  { label: '80-90', value: { start: 80, end: 90 } },
  { label: '90-100', value: { start: 90, end: 100 } },
];

interface SearchFormValues {
  reportDate?: dayjs.Dayjs;
  mode?: string;
  fleetName?: string;
  plateNumber?: string;
  riskLevel?: string;
  /** 多选：每项为区间对象的 JSON 字符串（与 SCORE_RANGE_OPTIONS 的 value 一致） */
  scoreRange?: string[];
}

const VehicleReport = () => {
  const [form] = Form.useForm<SearchFormValues>();
  const [searchParams, setSearchParams] = useState<Record<string, any>>({});

  const { page, limit, loading, data, totalCount, onPageChange } = useTable({
    requestFunc: carRiskListRequest,
    initRequest: true,
    initPageSize: 40,
    searchParams,
  });

  /**
   * 将表单的筛选值转换为接口需要的查询参数
   */
  const operateSearchParams = (values: SearchFormValues, isExport?: boolean): Record<string, any> => {
    const params: Record<string, any> = {};

    if (values.reportDate) {
      params.reportDate = dayjs(values.reportDate).format('YYYY-MM-DD');
    }

    if (values.mode) {
      params.mode = values.mode;
    }
    if (values.fleetName) {
      params.fleetName = values.fleetName.trim();
    }
    if (values.plateNumber) {
      params.plateNumber = values.plateNumber.replace(/\s+/g, '');
    }
    if (values.riskLevel) {
      params.riskLevel = values.riskLevel;
    }
    if (values.scoreRange && values.scoreRange.length > 0) {
      values.scoreRange.forEach((rangeStr, index) => {
        try {
          const range = JSON.parse(rangeStr) as CarRiskScoreRange;
          params[`scoreRanges[${index}].start`] = range.start;
          params[`scoreRanges[${index}].end`] = range.end;
        } catch (error) {
          message.error('车辆评分参数格式错误');
        }
      });
    }

    if (isExport) {
      params.export = true;
    }

    return params;
  };

  // 搜索
  const onSearch = (values: SearchFormValues) => {
    const newSearchParams = operateSearchParams(values);
    setSearchParams(newSearchParams);
  };

  // 重置
  const onReset = () => {
    form.resetFields();
    const values = form.getFieldsValue();
    const newSearchParams = operateSearchParams(values);
    setSearchParams(newSearchParams);
  };

  // 获取导出参数
  const getExportParams = () => {
    const values = form.getFieldsValue();
    return operateSearchParams(values, true);
  };

  // 表格列定义
  const columns = [
    {
      title: '日期',
      dataIndex: 'scoreDateRange',
      width: 200,
    },
    {
      title: '车辆等级',
      dataIndex: 'riskLevel',
      width: 100,
    },
    {
      title: '车辆',
      dataIndex: 'plateNumber',
      width: 130,
    },
    {
      title: '车辆类型',
      dataIndex: 'carTypeDesc',
      width: 120,
    },
    {
      title: '业务模式',
      dataIndex: 'mode',
      width: 120,
    },
    {
      title: '车队名称',
      dataIndex: 'fleetName',
      width: 180,
    },
    {
      title: '安全指数评分',
      dataIndex: 'safeScore',
      width: 120,
    },
    {
      title: '设备预警评分',
      dataIndex: 'deviceAlarmScore',
      width: 120,
    },
    {
      title: '精神状态评分',
      dataIndex: 'mentalStateScore',
      width: 120,
    },
    {
      title: '线路安全评分',
      dataIndex: 'lineSafeScore',
      width: 120,
    },
    {
      title: '驾驶习惯评分',
      dataIndex: 'driverHabitScore',
      width: 120,
    },
    {
      title: '工作强度评分',
      dataIndex: 'workIntensityScore',
      width: 120,
    },
    {
      title: '风险点评',
      dataIndex: 'riskLevelDesc',
      width: 120,
      render: (_: any, record: any) => record.riskLevel || '--',
    },
    {
      title: '车辆分数',
      dataIndex: 'carScore',
      width: 120,
      render: (_: any, record: any) => record.safeScore ?? '--',
    },
  ];

  return (
    <Card bordered={false} className={styles.fixPageContentBox}>
      <Form form={form} onFinish={onSearch} autoComplete="off" layout="vertical">
        <Row gutter={24} wrap>
          <Col span={4}>
            <Form.Item label="日期" name="reportDate">
              <DatePicker allowClear={false} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="业务模式" name="mode">
              <Select placeholder="请选择业务模式" allowClear style={{ width: '100%' }}>
                {MODE_OPTIONS.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="车队名称" name="fleetName">
              <Input placeholder="请输入车队名称（模糊搜索）" allowClear onChange={(e) => { const newV = e.target.value.replace(/\s+/g, ''); form.setFieldValue('fleetName', newV); }} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="车辆名称" name="plateNumber">
              <Input placeholder="请输入车辆名称（模糊搜索）" allowClear onChange={(e) => { const newV = e.target.value.replace(/\s+/g, ''); form.setFieldValue('plateNumber', newV); }} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label="车辆等级" name="riskLevel">
              <Select placeholder="请选择车辆等级" allowClear style={{ width: '100%' }}>
                {RISK_LEVEL_OPTIONS.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24} wrap>
          <Col span={4}>
            <Form.Item label="车辆评分" name="scoreRange">
              <Select mode="multiple" placeholder="请选择车辆评分" allowClear style={{ width: '100%' }}>
                {SCORE_RANGE_OPTIONS.map((item) => (
                  <Option key={item.label} value={JSON.stringify(item.value)}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item label=" ">
              <div className={styles.searchBox}>
                <Button type="primary" htmlType="submit">
                  搜索
                </Button>
                <Button onClick={onReset}>重置</Button>
                <ExportButton
                  url={carRiskListRequestPath}
                  fileName="车辆报告"
                  getParamsFunc={getExportParams}
                  requestMethod="GET"
                  permissionKey=""
                />
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <CommonTable
        rowKey="id"
        columns={columns}
        dataSource={data}
        pagination={{
          total: totalCount,
          current: page,
          pageSize: limit,
          onChange: onPageChange,
        }}
        bordered
        loading={loading}
      />
    </Card>
  );
};

export default VehicleReport;
